import React, { useState } from 'react';

import {
  Button,
  EmailField,
  TextField,
  WrapperField,
  useDataProvider,
  useNotify,
  useTranslate,
} from 'react-admin';
import { withLabel } from 'ui/hoc/with-label/withLabel';
import { UrlFieldWithLabel } from 'ui/atoms/url-field/UrlField';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import { CircularProgress } from '@mui/material';
import { HeliosDataProvider } from 'providers/admin/dataProvider';
import { useRealEstatePublicLink } from 'hooks/use-public-link/usePublicLink';

import { Container } from './InformationsSection.styles';
import { InformationsSectionProps } from './InformationsSection.types';

const TextFieldWithLabel = withLabel(TextField);
const EmailFieldWithLabel = withLabel(EmailField);
const WrapperFieldWithLabel = withLabel(WrapperField);

export const InformationsSection: React.FC<InformationsSectionProps> = ({
  adresse,
  propertyId,
  hunterFullName,
  hunterEmail,
  investorUrl,
  investorEmail,
  userFullName,
  searchMandate,
  realEstateTitle,
  secondInvestorFullName,
  observations,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const translate = useTranslate();
  const notify = useNotify();
  const { generateAndOpenPublicLink } = useRealEstatePublicLink();

  const dataProvider = useDataProvider<HeliosDataProvider>();

  const downloadDocumentFile = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();

    setIsLoading(true);

    if (!searchMandate?.id) {
      setIsLoading(false);
      return;
    }

    dataProvider
      .downloadDocument(searchMandate?.id)
      .catch((e: Error) =>
        notify(e.message, {
          type: 'error',
        })
      )
      .finally(() => setIsLoading(false));
  };

  return (
    <Container>
      <div className="address">
        <TextFieldWithLabel
          label={translate('transaction.show.section.label.projectAddress')}
          variant="body2"
          component="span"
          source="property.address"
          record={{ 'property.address': adresse }}
        />
      </div>

      <div className="investor">
        <UrlFieldWithLabel
          label={translate('transaction.show.section.label.investor')}
          source="user.fullName"
          record={{
            'user.fullName': `${userFullName}${
              secondInvestorFullName ? ` / ${secondInvestorFullName}` : ''
            }`,
          }}
          href={investorUrl}
        />
        {!!investorEmail && (
          <EmailFieldWithLabel
            label={translate('transaction.show.section.label.email')}
            source="user.email"
            record={{ 'user.email': investorEmail }}
          />
        )}
        {!!searchMandate && (
          <UrlFieldWithLabel
            disabled={isLoading}
            data-testid="searchMandate-download-link"
            label={translate('transaction.show.section.label.download_mandate')}
            source="searchMandate"
            record={{
              searchMandate: translate('transaction.show.documents.download'),
            }}
            icon={
              isLoading ? (
                <CircularProgress size={25} />
              ) : (
                <InsertDriveFileOutlinedIcon />
              )
            }
            onClick={downloadDocumentFile}
            href="#"
          />
        )}
      </div>

      <div className="property">
        <WrapperFieldWithLabel
          label={translate('transaction.show.section.label.projectLink')}
        >
          <Button
            onClick={() => generateAndOpenPublicLink(propertyId)}
            label={realEstateTitle}
          />
        </WrapperFieldWithLabel>
        <TextFieldWithLabel
          label={translate('transaction.show.section.label.mandator')}
          variant="body2"
          component="span"
          source="hunter.fullName"
          record={{ 'hunter.fullName': hunterFullName }}
        />
        <TextFieldWithLabel
          label={translate('transaction.show.section.label.mandatorEmail')}
          variant="body2"
          component="span"
          source="hunter.email"
          record={{ 'hunter.email': hunterEmail }}
        />
      </div>

      {observations ? (
        <div className="observations">
          <TextFieldWithLabel
            label="Observations"
            source="observations"
            record={{ observations: observations }}
          />
        </div>
      ) : null}
    </Container>
  );
};
